import React, { useEffect, useMemo, useState } from 'react';
import styles from './charts.module.css';

const HEIGHT = 240;
const MARGIN = { top: 12, right: 24, bottom: 28, left: 44 };

const niceStep = (max, count) => {
  const raw = max / count;
  const magnitude = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 5, 10].map((factor) => factor * magnitude).find((value) => value >= raw);
  return Math.max(step, 1);
};

// Callback ref so the observer attaches whenever the plot mounts (it is absent while there is no data).
const useWidth = () => {
  const [node, setNode] = useState(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    if (!node) return undefined;
    const observer = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width));
    observer.observe(node);
    return () => observer.disconnect();
  }, [node]);
  return [setNode, width];
};

/**
 * Multi-series line chart over time with a crosshair tooltip.
 * points: [{ period, ...values }], series: [{ key, label, color }],
 * formatPeriod(period, long) labels the x axis (long=false) and tooltip (long=true).
 */
const LineChart = ({ points, series, formatPeriod, emptyText = 'No data.' }) => {
  const [ref, width] = useWidth();
  const [hover, setHover] = useState(null);

  const geometry = useMemo(() => {
    if (!width || !points?.length) return null;
    const plotWidth = Math.max(width - MARGIN.left - MARGIN.right, 10);
    const plotHeight = HEIGHT - MARGIN.top - MARGIN.bottom;
    const max = Math.max(1, ...points.flatMap((point) => series.map((item) => point[item.key] || 0)));
    const step = niceStep(max, 4);
    const top = Math.ceil(max / step) * step;
    const x = (index) => MARGIN.left + (points.length === 1 ? plotWidth / 2 : (index / (points.length - 1)) * plotWidth);
    const y = (value) => MARGIN.top + plotHeight - (value / top) * plotHeight;
    const ticks = [];
    for (let value = 0; value <= top; value += step) ticks.push(value);
    const labelEvery = Math.max(1, Math.ceil(points.length / Math.max(1, Math.floor(plotWidth / 70))));
    return { plotWidth, x, y, ticks, labelEvery };
  }, [points, series, width]);

  if (!points?.length) return <p className={styles.empty}>{emptyText}</p>;

  const onMove = (event) => {
    if (!geometry) return;
    const box = event.currentTarget.getBoundingClientRect();
    const relative = (event.clientX - box.left - MARGIN.left) / geometry.plotWidth;
    const index = Math.round(relative * (points.length - 1));
    setHover(Math.min(points.length - 1, Math.max(0, index)));
  };

  const hovered = hover != null ? points[hover] : null;

  return (
    <div className={styles.lineChart}>
      <ul className={styles.legend}>
        {series.map((item) => (
          <li key={item.key}>
            <span className={styles.legendSwatch} style={{ background: item.color }} />
            {item.label}
          </li>
        ))}
      </ul>
      <div ref={ref} className={styles.plot}>
        {geometry ? (
          <svg
            width={width}
            height={HEIGHT}
            role="img"
            aria-label={series.map((item) => item.label).join(', ')}
            onMouseMove={onMove}
            onMouseLeave={() => setHover(null)}
          >
            {geometry.ticks.map((tick) => (
              <g key={tick}>
                <line x1={MARGIN.left} x2={width - MARGIN.right} y1={geometry.y(tick)} y2={geometry.y(tick)} className={styles.gridLine} />
                <text x={MARGIN.left - 8} y={geometry.y(tick)} className={styles.axisText} textAnchor="end" dominantBaseline="middle">
                  {tick.toLocaleString()}
                </text>
              </g>
            ))}
            {points.map((point, index) => (index % geometry.labelEvery === 0 ? (
              <text key={point.period} x={geometry.x(index)} y={HEIGHT - 8} className={styles.axisText} textAnchor="middle">
                {formatPeriod(point.period, false)}
              </text>
            ) : null))}
            {hovered ? (
              <line x1={geometry.x(hover)} x2={geometry.x(hover)} y1={MARGIN.top} y2={HEIGHT - MARGIN.bottom} className={styles.crosshair} />
            ) : null}
            {series.map((item) => (
              <polyline
                key={item.key}
                fill="none"
                stroke={item.color}
                strokeWidth="2"
                strokeLinejoin="round"
                strokeLinecap="round"
                points={points.map((point, index) => `${geometry.x(index)},${geometry.y(point[item.key] || 0)}`).join(' ')}
              />
            ))}
            {hovered ? series.map((item) => (
              <circle
                key={item.key}
                cx={geometry.x(hover)}
                cy={geometry.y(hovered[item.key] || 0)}
                r="4"
                fill={item.color}
                className={styles.marker}
              />
            )) : null}
          </svg>
        ) : null}
        {hovered && geometry ? (
          <div
            className={styles.tooltip}
            style={{
              left: geometry.x(hover),
              transform: `translateX(${geometry.x(hover) > width / 2 ? 'calc(-100% - 12px)' : '12px'})`,
            }}
          >
            <strong>{formatPeriod(hovered.period, true)}</strong>
            {series.map((item) => (
              <span key={item.key} className={styles.tooltipRow}>
                <span className={styles.legendSwatch} style={{ background: item.color }} />
                {item.label}
                <b>{(hovered[item.key] || 0).toLocaleString()}</b>
              </span>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default LineChart;
